<script>
export default {
	name: 'CrossSellTiles',
	inheritAttrs: false,
};
</script>

<script setup>
import VueSwiper from '@/components/VueSwiper.vue';
import CTAButton from '@/aem-components/CTAButton.vue';
import dayjs from 'dayjs';
import FontAwesomeLazy from '~/components/FontAwesomeLazy.vue';
import AppHyperlink from '@/components/AppHyperlink.vue';
import { isMobileViewport } from '~/logic/composables/breakpoints';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';

const props = defineProps({
	tileType: { type: String, default: 'Manual' },
	tileBehaviour: { type: String, default: 'Carousel' },
	isHideInDesktop: { type: Boolean, default: false },
	isHideInMobile: { type: Boolean, default: false },
	manualMultilist: { type: Array, default: () => [] },
	contentMultilist: { type: Array, default: () => ([]) },
	articleMultilist: { type: Array, default: () => ([]) },
	pageProperties: { type: Array, default: () => ([]) },
	componentIdAA: { type: String, default: '' },

	// below props currently use by Firefly
	carouselDesktopSlidesPerView: { type: Number, default: 0 },
	isClickable: { type: Boolean, default: true },
	descriptionTruncatedLine: { type: Number, default: 0 },
	isHideShowMore: { type: Boolean, default: false },
	maxSlidesToCenterSwiper: { type: Number, default: 0 },
});

/*
*	Return transformed multi-list based on tileType
*/
const multiListTile = computed(() => {
	if (props.tileType === 'content') {

		const transformedContentMultiList = props.pageProperties.map((item, index) => {
			return { ...item, ...props.contentMultilist[index] };
		});
		// console.log('content props', JSON.parse(JSON.stringify(transformedContentMultiList)));
		return transformedContentMultiList;

	} else if (props.tileType === 'article') {

		const transformedArticleMultiList = props.pageProperties.map((item, index) => {
			return { ...item, ...props.articleMultilist[index] };
		});

		return transformedArticleMultiList;

	} else {
		return props.manualMultilist;
	}
});

const crossSellTileRef = ref(null);
const { isEditorMode } = useGlobalAEMState();

const TILES_PER_LOAD = 6;
const currentTilesShown = ref(props.isHideShowMore ? multiListTile.value.length : TILES_PER_LOAD);

const siteName = window.siteName;

const crossSellTileTime = (time) => {
	return dayjs(time).format('DD MMM YYYY');
};

const isShowMoreButton = computed(() => {
	return currentTilesShown.value < multiListTile.value.length;
});

/*
*	swiperSlidesLimit =< number of slides, will apply "align-left" to swiper wrapper
*	swiperSlidesLimit > number of slides, will apply "align-center" to swiper wrapper
*/
const swiperSlidesLimit = computed(() => {
	let slidesLimit = 4;

	if (!isEditorMode.value && props.maxSlidesToCenterSwiper) {
		slidesLimit = props.maxSlidesToCenterSwiper;
	}

	return slidesLimit;
});

const handleObserverUpdate = (swiperInstance) => {
	if (!swiperInstance.$wrapperEl || swiperInstance.$wrapperEl.length === 0) return;
	swiperInstance.$wrapperEl.at(0).style.height = 'auto';
};

</script>

<template>
<template v-if="isEditorMode && props.isHideInDesktop">
	<div class="generic-container py-10 text-center" :data-use-theme="siteName">
		<p>Hidden in: <span v-if="props.isHideInDesktop">Desktop</span> <span v-if="props.isHideInMobile">, Mobile</span></p>
	</div>
</template>

<template v-else>
	<div
		v-if="(isMobileViewport && !props.isHideInMobile) || (!isMobileViewport && !props.isHideInDesktop)"
		class="CrossSellTiles"
		:data-use-theme="siteName"
	>
	
		<VueSwiper
			v-if="props.tileBehaviour === 'carousel'"
			ref="crossSellTileRef"
			:slidesPerView="multiListTile.length === 1 ? multiListTile.length : 1.3"
			:dormantMode="isEditorMode"
			:dormantModeEnableGridLayout="true"
			:spaceBetween="24"
			:navigation="!isMobileViewport && multiListTile.length > 3"
			:autoHeight="!isMobileViewport"
			:class="{
				'alignCentered': !isMobileViewport && multiListTile.length < swiperSlidesLimit,
			}"
			:breakpoints="{
				1024: {
					slidesPerView: props.carouselDesktopSlidesPerView ? props.carouselDesktopSlidesPerView : 3.3,
					spaceBetween: multiListTile.length === 1 ? 0 : 24,
				},
			}"
			:observer="true"
			:observeParents="true"
			:observeSlideChildren="true"
			@observer-update="handleObserverUpdate"
		>
			<component
				:is="carouselTileItem.tileUrl || carouselTileItem.contentPagePath ? AppHyperlink : 'div'"
				v-for="(carouselTileItem, carouselTileIndex) in multiListTile"
				:key="carouselTileIndex"
				v-aa="carouselTileItem.tileUrl || carouselTileItem.contentPagePath ? [
					{
						clickName: carouselTileItem.tileTitle || carouselTileItem.title,
						clickComponentType: 'URL',
						componentName: 'CrossSellTiles',
						componentID: props.componentIdAA,
					},
					{
						name: carouselTileItem.tileTitle || carouselTileItem.title,
						type: 'exit',
					},
				] : null"
				:href="carouselTileItem.tileUrl || carouselTileItem.contentPagePath"
				:target="carouselTileItem.openInNewTab ? '_blank' : '_self'"
				class="cross-sell-wrapper h-full overflow-hidden flex-nowrap"
				:class="{
					'content': props.tileType !== 'article',
					'not-clickable': !props.isClickable,
				}"
			>
				<div
					class="cross-sell-thumbnail relative w-full"
					:style="{ 'background-image': `url(${carouselTileItem.tileImage})` }"
					:class="{ 'rounded-3xl': props.tileType === 'article' }"
					:title="carouselTileItem.tileImageAltText"
				>
				</div>
				<div
					class="cross-sell-body w-full"
					:class="[ props.tileType !== 'article' ? 'px-6 pt-6 pb-8 bg-neutral-white-base' : 'py-6' ]"
				>
					<h5
						v-if="props.tileType !== 'article'"
						class="cross-sell-body-tile-title font-bold"
					>
						{{ carouselTileItem.tileTitle || carouselTileItem.title }}
					</h5>
					<!-- For Article title -->
					<p
						v-else
						class="cross-sell-body-tile-title font-semibold text-xs text-primary-blue-base"
					>
						<span class="mr-2">{{ crossSellTileTime(carouselTileItem.articleDate) }}</span>
						<span v-if="carouselTileItem.categoryTags?.length">|</span>
						
						<template
							v-for="(categoryTag, categoryIndex) in carouselTileItem.categoryTags"
							:key="categoryIndex"
						>
							<span class="ml-2">{{ categoryTag }}</span>
						</template>
					</p>
					<div
						v-if="props.tileType !== 'article' && (carouselTileItem.iconFontAwsomeId || carouselTileItem.tileIconText || carouselTileItem.articleDate)"
						class="cross-sell-body-tile-icon-text text-sm mt-4"
					>
						<FontAwesomeLazy
							v-if="carouselTileItem.tileIconType === 'iconFontAwsomeId'"
							:iconId="carouselTileItem.iconFontAwsomeId ? carouselTileItem.iconFontAwsomeId : null"
							class="tileFontAwesomeIcon mr-3 rtl:ml-3 rtl:mr-0"
						/>
						<span v-if="carouselTileItem.tileIconText">{{ carouselTileItem.tileIconText }}</span>
						<span v-if="carouselTileItem.articleDate">{{ $t("Sale until") }} {{ crossSellTileTime(carouselTileItem.articleDate) }}</span>
					</div>
					<div
						v-if="carouselTileItem.tileDescription"
						class="cross-sell-body-tile-description"
						:class="[
							props.tileType === 'article' ? 'mt-3 text-xl text-primary-black-base' : 'mt-4 text-sm',
							props.descriptionTruncatedLine ? `line-clamp-${props.descriptionTruncatedLine}` : '',
						]"
					>
						<template v-if="props.tileType !== 'article'">
							<p v-html-sanitize="carouselTileItem.tileDescription || carouselTileItem.title"></p>
						</template>
						<template v-else>
							<AppHyperlink
								:href="carouselTileItem.articlePagePath"
								class="text-primary-black-base"
							>
								<span v-html-sanitize="carouselTileItem.tileDescription || carouselTileItem.title"></span>
							</AppHyperlink>
						</template>
					</div>
					<div
						v-if="props.tileType !== 'article' && (carouselTileItem.tileUrl || carouselTileItem.contentPagePath)"
						class="cross-sell-body-tile-text-links text-sm mt-6"
					>
						<AppHyperlink
							:href="carouselTileItem.tileUrl || carouselTileItem.contentPagePath"
							:target="carouselTileItem.openInNewTab ? '_blank' : '_self'"
							class="cross-sell-body-tile-cta"
						>
							<span class="font-semibold">{{ carouselTileItem.ctaText }}</span>
							<icon-far-arrow-right class="tile-arrow-right ml-2.5 rtl:mr-2.5 inline-block" />
						</AppHyperlink>
					</div>
				</div>
			</component>
		</VueSwiper>
		<div
			v-else
			class="stacked-grid"
			:class="[{ 'grid >lg:gap-x-6 gap-y-7 grid-cols-3 md:grid-cols-1 px-2.5': props.tileBehaviour === 'stacked' }]"
		>
			<component
				:is="stackTileItem.tileUrl || stackTileItem.contentPagePath ? AppHyperlink : 'div'"
				v-for="(stackTileItem, stackTileIndex) in multiListTile.slice(0, currentTilesShown)"
				:key="stackTileIndex"
				v-aa="stackTileItem.tileUrl || stackTileItem.contentPagePath ? [
					{
						clickName: stackTileItem.tileTitle || stackTileItem.title,
						clickComponentType: 'URL',
						componentName: 'CrossSellTiles',
						componentID: props.componentIdAA,
					},
					{
						name: stackTileItem.tileTitle || stackTileItem.title,
						type: 'exit',
					},
				] : null"
				:href="stackTileItem.tileUrl || stackTileItem.contentPagePath"
				:target="stackTileItem.openInNewTab ? '_blank' : '_self'"
				class="cross-sell-wrapper overflow-hidden flex-nowrap"
				:class="{
					'content': props.tileType !== 'article',
					'not-clickable': !props.isClickable,
				}"
			>
				<div
					class="cross-sell-thumbnail relative w-full"
					:style="{ 'background-image': `url(${stackTileItem.tileImage})` }"
					:class="{ 'rounded-3xl': props.tileType === 'article' }"
					:title="stackTileItem.tileImageAltText"
				>
					<!-- <img
						src="@/assets/banner/herobanner-sample-1-desktop.jpg"
						alt=""
						class="absolute top-0"
					/> -->
				</div>
				<div
					class="cross-sell-body w-full"
					:class="[ props.tileType !== 'article' ? 'px-6 pt-6 pb-8 bg-neutral-white-base' : 'py-6' ]"
				>
					<h5
						v-if="props.tileType !== 'article'"
						class="cross-sell-body-tile-title font-bold"
					>
						{{ stackTileItem.tileTitle || stackTileItem.title }}
					</h5>
					<!-- For Article title -->
					<p
						v-else
						class="cross-sell-body-tile-title font-semibold text-xs text-primary-blue-base"
					>
						<span class="mr-2">{{ crossSellTileTime(stackTileItem.articleDate) }}</span>
						<span v-if="stackTileItem.categoryTags?.length">|</span>
						
						<template
							v-for="(categoryTag, categoryIndex) in stackTileItem.categoryTags"
							:key="categoryIndex"
						>
							<span class="ml-2">{{ categoryTag }}</span>
						</template>
					</p>
					<div
						v-if="props.tileType !== 'article' && (stackTileItem.iconFontAwsomeId || stackTileItem.tileIconText || stackTileItem.articleDate)"
						class="cross-sell-body-tile-icon-text text-sm mt-4"
					>
						<FontAwesomeLazy
							v-if="stackTileItem.tileIconType === 'iconFontAwsomeId'"
							:iconId="stackTileItem.iconFontAwsomeId ? stackTileItem.iconFontAwsomeId : null"
							class="tileFontAwesomeIcon mr-3 rtl:ml-3 rtl:mr-0"
						/>
	
						<span v-if="stackTileItem.tileIconText">{{ stackTileItem.tileIconText }}</span>
						<span v-if="stackTileItem.articleDate">{{ $t("Sale until") }} {{ crossSellTileTime(stackTileItem.articleDate) }}</span>
					</div>
					<div
						v-if="stackTileItem.tileDescription"
						class="cross-sell-body-tile-description"
						:class="[
							props.tileType === 'article' ? 'mt-3 text-xl text-primary-black-base' : 'mt-4 text-sm',
							props.descriptionTruncatedLine ? `line-clamp-${props.descriptionTruncatedLine}` : '',
						]"
					>
						<template v-if="props.tileType !== 'article'">
							<p v-html-sanitize="stackTileItem.tileDescription || stackTileItem.title"></p>
						</template>
						<template v-else>
							<AppHyperlink
								:href="stackTileItem.articlePagePath"
								class="text-primary-black-base"
							>
								<!-- {{ stackTileItem.tileDescription || stackTileItem.title }} -->
								<span v-html-sanitize="stackTileItem.tileDescription || stackTileItem.title"></span>
							</AppHyperlink>
						</template>
					</div>
					<div
						v-if="props.tileType !== 'article' && (stackTileItem.tileUrl || stackTileItem.contentPagePath)"
						class="cross-sell-body-tile-text-links text-sm mt-6"
					>
						<AppHyperlink
							:href="stackTileItem.tileUrl || stackTileItem.contentPagePath"
							:target="stackTileItem.openInNewTab ? '_blank' : '_self'"
							class="cross-sell-body-tile-cta"
						>
							<span class="font-semibold">{{ stackTileItem.ctaText }}</span>
							<icon-far-arrow-right class="tile-arrow-right ml-2.5 rtl:mr-2.5 inline-block" />
						</AppHyperlink>
					</div>
				</div>
			</component>
		</div>
		
		<div v-if=" props.tileBehaviour === 'stacked' && isShowMoreButton" class="show-more-cta mt-10 md:mt-8">
			<CTAButton
				v-aa="[
					{
						clickName: $t('Show more'),
						clickComponentType: 'Button',
						componentName: 'CTAButton',
						componentID: props.componentIdAA,
					},
					{
						name: $t('Show more'),
						type: 'other',
					},
				]"
				:ctaText="$t('Show more')"
				:sizeType="isMobileViewport ? 'small' : 'medium'"
				containerClass="font-semibold"
				@click="currentTilesShown = currentTilesShown + TILES_PER_LOAD"
			/>
		</div>
	</div>
</template>
</template>

<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.CrossSellTiles {

	:deep(.swiper-slide) {
		height: 100%;
		
		@media #{var.$query-max-md} {
			height: auto;
		}
	}

	:deep(.swiper-horizontal) {
		padding: 10px;
	}

	:deep(.swiper-button-prev) {
		height: 56px;
		width: 56px;
		border: 1px solid var.$neutral-grey-light;
		border-radius: 28px;
		@apply bg-primary-black-base/20;
		transition: opacity .25s ease-in-out;
		left: 83px;
		opacity: 0;

		&:hover {
			@apply bg-primary-black-base/60;
		}
		
		&:hover::after {
			transform: translateX(-2px);
		}
 
		&::after {
			content: "";
			background-image: url('~/assets/carousel/chevron-left.svg');
			background-size: cover;
			background-position: center;
			background-color: transparent;
			height: 26px;
			width: 13px;
			transition: 200ms ease-in-out;
		}
	}

	:deep(.swiper-button-next) {
		height: 56px;
		width: 56px;
		border: 1px solid var.$neutral-grey-light;
		border-radius: 28px;
		@apply bg-primary-black-base/20;
		opacity: 0;
		right: 83px;
		transition: 200ms ease-in-out;

		&:hover {
			@apply bg-primary-black-base/60;
		}
		
		&:hover::after {
			transform: rotate(180deg) translateX(-2px);
		}

		&::after {
			content: "";
			background-image: url('~/assets/carousel/chevron-left.svg');
			background-size: cover;
			background-position: center;
			background-color: transparent;
			height: 26px;
			width: 13px;
			transform: rotate(180deg);
			transition: 200ms ease-in-out;
		}
	}

	:deep(.tileFontAwesomeIcon) {
		@apply inline-block text-[#CFAF88];
		
		svg {
			height: 14px;
			width: auto;
			@apply fill-current;
		}
	}
	
	&:hover {
		:deep(.swiper-button-prev) {
			opacity: 1;
		}

		:deep(.swiper-button-next) {
			opacity: 1;
		}
	}

	:deep(.swiper-wrapper) {
		
		.swiper-slide {
			overflow: visible;
		}

		.swiper-slide:last-child {
			margin-right: 0!important;
		}
	}

	:deep(.alignCentered) {
		.swiper-wrapper {
			justify-content: center;
		}
	}

	.cross-sell-body-tile-description {
		flex-grow: 1;
	}
	
	.show-more-cta {
		:deep(.CTAButton) {
			@media #{var.$query-max-md} {
				min-width: 200px;
			}
		}
	}
}

.swiper-editor-mode {
	display: grid;
	gap: 1rem;
	grid-template-columns: repeat(auto-fit, minmax(313px, 1fr));
}

.cross-sell-wrapper {
	display: flex;
	flex-direction: column;
	height: 100%;
	flex-grow: 1;
	color: var(--primary-black-base);
	transition: .25s ease-in-out;
	
	&:hover {
		text-decoration: none;
		
		.tile-arrow-right {
			@apply transform translate-x-0.5;
			
			html[dir="rtl"] & {
				@apply transform -translate-x-0.5 -rotate-180;
			}
		}
		
	}

	&.content {
		border-radius: 24px;
		border: solid 1px var(--neutral-grey-light);
		
		&:hover:not(.not-clickable) {
			@apply shadow-type-a;
		}
	}
}

.cross-sell-thumbnail {
	padding-top: 66.66%;
	// background-color: var.$secondary-teal-base;
	background-image: url('@/assets/banner/herobanner-sample-1-desktop.jpg');
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;

	// &::before {
	// 	content: "";
	// 	padding-top: 66.66%;
	// 	display: inherit;
	// 	background-color: var(--secondary-teal-base);
	// }
}

.cross-sell-body {
	// padding: 24px 24px 32px 24px;
	display: flex;
	flex-direction: column;
	flex-grow: 1;

	.AppHyperlink {

		.tile-arrow-right {
			fill: var(--primary-blue-base);
		}
	}
}

.cross-sell-body-tile-title {
	word-break: break-word;

	// &.article {
	// 	color: #CFAF88;
	// }
}

.cross-sell-body-tile-cta {
	.tile-arrow-right {
		transition: transform .3s ease-in-out;
		
		html[dir="rtl"] & {
			@apply transform -rotate-180;
		}
	}
}

[data-use-theme="MAG"] {
	.AppHyperlink {
		.tile-arrow-right {
			fill: var(--primary-midnightgreen-base);
		}

		&:hover .tile-arrow-right{
			fill: var(--primary-midnightgreen-dark);
		}
	}
}

[data-use-theme="MHH"] {
	.AppHyperlink {
		--a-tag-color: var(--secondary-mhh-teal-dark);
		--a-tag-color-hover: var(--secondary-mhh-teal-dark);
		
		.tile-arrow-right {
			fill: var(--secondary-mhh-teal-dark);
		}
	}
}

[data-use-theme="firefly"] {
	.AppHyperlink {
		--a-tag-color: var(--primary-firefly-orange-base);
		--a-tag-color-hover: var(--secondary-firefly-orange-medium);
		
		.tile-arrow-right {
			fill: var(--primary-firefly-orange-base);
		}
	}

	:deep(.tileFontAwesomeIcon) {
		svg {
			fill: var(--secondary-firefly-orange-base);
		}
	}
}
</style>
